import type { DashboardRanking, DashboardSummary, DashboardTrends } from './types';

export const dashboardMetaFixture = Object.freeze({
  snapshotId: 'd0000000-0000-4000-8000-000000000001',
  reportingAsOf: '2026-07-23T08:00:00Z',
  refreshedAt: '2026-07-23T08:01:00Z',
  stale: false,
  timezone: 'Asia/Bangkok',
  timeBasis: 'PLAY_AT',
  normalizedFilters: {},
} as const);

export const dashboardMockFilterFixture = Object.freeze({
  partnerId: '30000000-0000-4000-8000-000000000001',
  agentId: '40000000-0000-4000-8000-000000000001',
  gameTypeId: '50000000-0000-4000-8000-000000000001',
});

export const dashboardSummaryFixture: DashboardSummary = Object.freeze({
  transactionCount: 12_640,
  calculatedTransactionCount: 12_184,
  rejectedTransactionCount: 318,
  failedTransactionCount: 138,
  activeUserCount: 3_284,
  registrationCount: 486,
  withdrawalExceptionCount: 17,
  currencies: [
    {
      currencyCode: 'THB',
      playAmount: '18425000.000000',
      agentCommissionGross: '552750.000000',
      affiliateCommissionGross: '368500.000000',
      commissionReversals: '-18420.000000',
      commissionAdjustments: '6500.000000',
      netCommission: '909330.000000',
      walletAvailable: '4620000.000000',
      walletReserved: '385000.000000',
      withdrawalRequested: '925000.000000',
      withdrawalReserved: '310000.000000',
      withdrawalCompleted: '568000.000000',
      withdrawalReleased: '32000.000000',
      withdrawalUnknown: '15000.000000',
    },
    {
      currencyCode: 'USD',
      playAmount: '284500.000000',
      agentCommissionGross: '8535.000000',
      affiliateCommissionGross: '5690.000000',
      commissionReversals: '-320.000000',
      commissionAdjustments: '125.000000',
      netCommission: '14030.000000',
      walletAvailable: '72150.000000',
      walletReserved: '8240.000000',
      withdrawalRequested: '18100.000000',
      withdrawalReserved: '6200.000000',
      withdrawalCompleted: '10900.000000',
      withdrawalReleased: '700.000000',
      withdrawalUnknown: '300.000000',
    },
  ],
  apiHealth: {
    status: 'operational' as const,
    latencyMs: 84,
    successRate: 99.98,
    checkedAt: '2026-07-23T08:01:00Z',
  },
  meta: dashboardMetaFixture,
  requestId: 'mock-dashboard-summary',
});

export const dashboardTrendsFixture: DashboardTrends = Object.freeze({
  points: [
    trend(
      '2026-07-20',
      'THB',
      4020,
      140,
      '5840000.000000',
      '175200.000000',
      '116800.000000',
      '168000.000000',
    ),
    trend(
      '2026-07-21',
      'THB',
      4260,
      164,
      '6185000.000000',
      '185550.000000',
      '123700.000000',
      '190000.000000',
    ),
    trend(
      '2026-07-22',
      'THB',
      4360,
      182,
      '6400000.000000',
      '192000.000000',
      '128000.000000',
      '210000.000000',
    ),
    trend(
      '2026-07-20',
      'USD',
      780,
      42,
      '88200.000000',
      '2646.000000',
      '1764.000000',
      '3200.000000',
    ),
    trend(
      '2026-07-21',
      'USD',
      820,
      48,
      '94100.000000',
      '2823.000000',
      '1882.000000',
      '3600.000000',
    ),
    trend(
      '2026-07-22',
      'USD',
      860,
      54,
      '102200.000000',
      '3066.000000',
      '2044.000000',
      '4100.000000',
    ),
  ],
  grain: 'DAY',
  meta: dashboardMetaFixture,
  requestId: 'mock-dashboard-trends',
});

export const dashboardTopGamesFixture: DashboardRanking = Object.freeze({
  rows: [
    ranking(
      '10000000-0000-4000-8000-000000000001',
      'SLOT-LOTUS',
      'Golden Lotus',
      null,
      'THB',
      2840,
      '4280000.000000',
      '214000.000000',
    ),
    ranking(
      '10000000-0000-4000-8000-000000000002',
      'LIVE-TGR',
      'Tiger Baccarat',
      null,
      'THB',
      1920,
      '3750000.000000',
      '187500.000000',
    ),
    ranking(
      '10000000-0000-4000-8000-000000000003',
      'SLOT-ORB',
      'Orbit Riches',
      null,
      'USD',
      1640,
      '86200.000000',
      '4310.000000',
    ),
  ],
  meta: dashboardMetaFixture,
  requestId: 'mock-dashboard-games',
});

export const dashboardTopAffiliatesFixture: DashboardRanking = Object.freeze({
  rows: [
    ranking(
      '20000000-0000-4000-8000-000000000001',
      null,
      'Lotus Partner',
      'lotus_partner',
      'THB',
      2460,
      '3580000.000000',
      '178000.000000',
    ),
    ranking(
      '20000000-0000-4000-8000-000000000002',
      null,
      'Siam Growth',
      'siam_growth',
      'THB',
      2140,
      '3240000.000000',
      '161000.000000',
    ),
    ranking(
      '20000000-0000-4000-8000-000000000003',
      null,
      'Orbit Network',
      'orbit_network',
      'USD',
      1280,
      '76400.000000',
      '3820.000000',
    ),
  ],
  meta: dashboardMetaFixture,
  requestId: 'mock-dashboard-affiliates',
});

function trend(
  date: string,
  currencyCode: string,
  transactionCount: number,
  registrationCount: number,
  playAmount: string,
  agentCommission: string,
  affiliateCommission: string,
  completedWithdrawalAmount: string,
) {
  return {
    periodStart: `${date}T00:00:00+07:00`,
    periodEnd: `${date}T23:59:59+07:00`,
    currencyCode,
    transactionCount,
    registrationCount,
    playAmount,
    agentCommission,
    affiliateCommission,
    completedWithdrawalAmount,
  } as const;
}

function ranking(
  id: string,
  code: string | null,
  name: string | null,
  username: string | null,
  currencyCode: string,
  transactionCount: number,
  playAmount: string,
  totalCommission: string,
) {
  return {
    id,
    code,
    name,
    username,
    currencyCode,
    transactionCount,
    playAmount,
    totalCommission,
  } as const;
}
